import { AISearch } from '@tableau/ai-search';
import { HeaderLabelChipStyle } from '@tableau/data-catalog-shared';
import React from 'react';
import { RouteComponentProps, withRouter } from 'react-router';

import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { SessionUserSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { siteUrlsSelector } from '~src/bootstrap/redux/selectors/SiteUrlsSelectors';
import { getAppStore } from '~src/bootstrap/redux/store/AppStore';
import { LabelWrapper } from '~src/catalog/CatalogLabelsHookHelper';
import { Telemetry } from '~src/commonInfra/services/Telemetry';
import { ToasterService, ToastType } from '~src/commonInfra/services/ToasterService';
import { ContentFiltersKeys } from '~src/spacesPlatform/models/ContentFilters';
import { IContentInfo } from '~src/spacesPlatform/models/IContentInfo';

export interface AISearchRenderOptions {
    query: string;
    xsrfToken?: string;
    onClose?: () => void;
}
export interface AISearchHostComponentProps {
    locationSearch: string;
    xsrfToken?: string;
}

export function getSearchQueryFromLocationSearch(locationSearch: string): string {
    const params = new URLSearchParams(locationSearch || '');
    return (params.get(ContentFiltersKeys.search) || '').trim();
}

function isAISearchEnabled(): boolean {
    return FeatureFlags.ces_AISearchEnabled && !!SessionUserSelectors.canUseAISearch(getAppStore()!.getState());
}

export const AISearchHostComponent: React.FunctionComponent<AISearchHostComponentProps> = (props) => {
    const query = getSearchQueryFromLocationSearch(props.locationSearch);
    const [dismissedQuery, setDismissedQuery] = React.useState('');

    React.useEffect(() => {
        if (query !== dismissedQuery) {
            setDismissedQuery('');
        }
    }, [dismissedQuery, query]);

    if (!isAISearchEnabled() || query.length === 0 || dismissedQuery === query) {
        return null;
    }

    const siteUrls = siteUrlsSelector(getAppStore()!.getState());
    return (
        <AISearch
            options={{
                query,
                telemetry: {
                    recordEvent: (eventName: string, properties?: Record<string, unknown>) => {
                        Telemetry.sendEvent<any>({
                            type: eventName,
                            ...properties
                        });
                    }
                },
                toastMessageHandler: (message: string) => {
                    ToasterService.instance.pop({
                        timeoutDelay: 5000,
                        text: message,
                        type: ToastType.Success,
                    });
                }
            }}
            siteUrls={siteUrls}
            renderWorkbookLabel={(workbook) => {
                // Adapter: AISearch hands us a TableauWorkbook; LabelWrapper wants IContentInfo.
                // LabelWrapper (with isEager) only reads id/luid/contentType/name -- updatedAt is
                // never consumed, so the non-null assertion just satisfies IContentInfo's required
                // updatedAt: Date. Do not read updatedAt off this object elsewhere.
                const workbookContentItem: IContentInfo<'workbook'> = { ...workbook, updatedAt: workbook.updatedAt! };
                return <LabelWrapper itemInfo={workbookContentItem} isEager={true} kind={'chip'}
                    wrapper={(e) => {
                        return <HeaderLabelChipStyle>{e}</HeaderLabelChipStyle>;
                    }} />;
            }}

        />
    );
};

const RoutedAISearchHost: React.FunctionComponent<RouteComponentProps> = (props) => (
    <AISearchHostComponent locationSearch={props.location.search} />
);

export const AISearchHost: React.ComponentClass = withRouter(RoutedAISearchHost);
